/**
 * InlineStatusMenu
 *
 * Shared inline status editor: a colored trigger label + chevron that opens a
 * Mantine `Menu` of selectable statuses. Used by both the task detail panel
 * (`TaskGeneralInfo`) and the obstruction detail panel (`ObstructionGeneralInfo`)
 * — the caller renders the colored `triggerLabel` (owning its own color token)
 * and supplies the option list; this component owns the menu chrome, trigger
 * a11y, portal/z-index, and the optional active-item indicator.
 *
 * In `readOnly` mode it renders the label only (no menu) — the permission
 * decision belongs to the caller; the server remains the source of truth.
 */
import type { ReactNode } from 'react';
import { Group, Menu } from '@mantine/core';
import { IconCheck, IconChevronDown } from '@tabler/icons-react';

import { Z_INDEX_PANEL_DROPDOWN } from '@/constants/zIndex';

import styles from './InlineStatusMenu.module.css';

const ICON_SIZE_CHEVRON = 12;
const ICON_SIZE_CHECK = 14;
const TRIGGER_GAP = 4;
const MENU_POSITION = 'bottom-start' as const;

export interface InlineStatusOption<T extends string> {
  value: T;
  label: string;
  /** Marks the current selection — shows a check icon + active styling. */
  active?: boolean;
}

interface InlineStatusMenuProps<T extends string> {
  /** Colored status label rendered by the caller (controls the color token). */
  triggerLabel: ReactNode;
  options: ReadonlyArray<InlineStatusOption<T>>;
  onSelect: (value: T) => void;
  /** When true, renders the label only (no interactive menu). */
  readOnly?: boolean;
}

export function InlineStatusMenu<T extends string>({
  triggerLabel,
  options,
  onSelect,
  readOnly = false,
}: InlineStatusMenuProps<T>) {
  if (readOnly) {
    return <>{triggerLabel}</>;
  }

  return (
    <Menu
      position={MENU_POSITION}
      withinPortal
      zIndex={Z_INDEX_PANEL_DROPDOWN}
      classNames={{
        dropdown: styles.statusDropdown,
        item: styles.statusMenuItem,
      }}
    >
      <Menu.Target>
        <Group gap={TRIGGER_GAP} className={styles.statusTrigger}>
          {triggerLabel}
          <IconChevronDown
            size={ICON_SIZE_CHEVRON}
            className={styles.statusChevron}
          />
        </Group>
      </Menu.Target>
      <Menu.Dropdown>
        {options.map((option) => (
          <Menu.Item
            key={option.value}
            onClick={() => onSelect(option.value)}
            className={option.active ? styles.activeMenuItem : undefined}
            rightSection={
              option.active ? (
                <IconCheck
                  size={ICON_SIZE_CHECK}
                  className={styles.statusCheckIcon}
                />
              ) : undefined
            }
          >
            {option.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}
